import { ReloadOutlined } from '@ant-design/icons';
import { Button, Flex, Result, Spin, Typography } from '@yimoka/antd';
import {
  PatternBrowserRouter,
  useLocation as useMiniLocation,
  useMiniNavigate,
} from '@yimoka/mini-router';
import { observer } from '@yimoka/react';
import { isSuccess } from '@yimoka/shared';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import {
  loadAdminRelationBadgeCounts,
  refreshAdminRelationBadgeCounts,
} from '@/entities/admin-relation-badge';
import { authAccountEntity } from '@/entities/auth-account';
import { useTranslation } from '@/locales/i18n';
import { getCurrentLanguage, subscribeLanguage } from '@/locales/language';
import { createRemoteComponent } from '@/mf/load-remote';
import type { AdminRelationBadgeCounts, TenantAdminRemoteProps } from '@/mf/types';
import { registerAdminBaseLocales } from '@/modules/admin/locales/register';
import { TenantWorkspacePage } from '@/pages/workspace';
import {
  getStaffToken,
  getTenantID,
  getUserToken,
  setTenant,
  setTenantID,
} from '@/session/auth-session';
import { AdminLayout } from '@/shells/admin';
import {
  findAdminMenuTrail,
  normalizeAdminMenus,
  type AdminMenuItem,
} from '@/shells/admin/menu';

const TenantAdminRemote = createRemoteComponent<TenantAdminRemoteProps>('tenant-web-admin/App');
const TenantHrAdminRemote = createRemoteComponent<TenantAdminRemoteProps>('tenant-web-hr-admin/App');
const ADMIN_RELATION_PATH = '/admin/system/relation/tenant';
const TENANT_DESKTOP_PAGE_CLASS = 'tenant-desktop-page';

registerAdminBaseLocales();

interface TenantAdminContentProps {
  menus: AdminMenuItem[];
  onRefreshRelationBadges?: () => Promise<boolean>;
  pageHeaderTarget: HTMLDivElement | null;
  relationBadgeCounts?: AdminRelationBadgeCounts;
  tenantID: string;
}

/** TenantAdminContent 按管理台子路由选择通用或 HR Remote，接口权限统一交由后端裁决。 */
function TenantAdminContent({
  menus,
  onRefreshRelationBadges,
  pageHeaderTarget,
  relationBadgeCounts,
  tenantID,
}: TenantAdminContentProps) {
  const location = useLocation();
  const isHrRoute = location.pathname === '/admin/hr' || location.pathname.startsWith('/admin/hr/');
  if (isHrRoute) return <TenantHrAdminRemote menus={menus} pageHeaderTarget={pageHeaderTarget} tenantID={tenantID} />;
  return (
    <TenantAdminRemote
      menus={menus}
      pageHeaderTarget={pageHeaderTarget}
      relationBadgeCounts={relationBadgeCounts}
      tenantID={tenantID}
      onRefreshRelationBadges={onRefreshRelationBadges}
    />
  );
}

/** TenantAdminPage 加载导航菜单后挂载租户管理台 Remote，空菜单不阻断直接路由访问。 */
export const TenantAdminPage = observer(({
  remotesReady,
  tenantID,
}: {
  remotesReady: boolean;
  tenantID: string;
}) => {
  const { t } = useTranslation();
  const language = useSyncExternalStore(subscribeLanguage, getCurrentLanguage, getCurrentLanguage);
  const store = useMemo(() => authAccountEntity.getMyMenusStore(tenantID, 'console'), [tenantID]);
  const tenantInfoStore = useMemo(
    () => authAccountEntity.getTenantInfoStore(tenantID),
    [tenantID],
  );
  const menus = useMemo(
    () => (isSuccess(store.response) ? normalizeAdminMenus(store.response.data ?? []) : []),
    [store.response],
  );
  const tenantInfo = isSuccess(tenantInfoStore.response)
    ? tenantInfoStore.response.data
    : undefined;
  const relationMenu = useMemo(() => {
    const trail = findAdminMenuTrail(menus, ADMIN_RELATION_PATH);
    const matched = trail[trail.length - 1];
    return matched?.path === ADMIN_RELATION_PATH ? matched : undefined;
  }, [menus]);
  const [relationBadgeCounts, setRelationBadgeCounts] = useState<AdminRelationBadgeCounts>();

  /** publishRelationBadges 仅在租户仍匹配时发布角标，失败时保留最近一次成功值。 */
  const publishRelationBadges = useCallback(async (
    loader: (currentTenantID: string) => Promise<AdminRelationBadgeCounts | undefined>,
  ) => {
    if (!tenantID || !relationMenu) return false;
    const counts = await loader(tenantID);
    if (!counts || getTenantID() !== tenantID) return false;
    setRelationBadgeCounts(counts);
    return true;
  }, [relationMenu, tenantID]);

  /** loadRelationBadges 供 Host 首次挂载复用同租户正在进行的 Count 请求。 */
  const loadRelationBadges = useCallback(
    () => publishRelationBadges(loadAdminRelationBadgeCounts),
    [publishRelationBadges],
  );

  /** refreshRelationBadges 供业务操作后强制读取操作完成后的最新 Count。 */
  const refreshRelationBadges = useCallback(
    () => publishRelationBadges(refreshAdminRelationBadgeCounts),
    [publishRelationBadges],
  );

  useEffect(() => {
    if (!remotesReady || !tenantID) return;
    void store.fetch();
    // 租户名称独立加载，失败时不阻塞权限菜单和管理台主体，头部继续使用会话或 tenantID 兜底。
    void tenantInfoStore.fetch().then((tenantInfoResponse) => {
      if (getTenantID() === tenantID && isSuccess(tenantInfoResponse) && tenantInfoResponse.data) {
        setTenant(tenantInfoResponse.data);
      }
    });
  }, [language, remotesReady, store, tenantID, tenantInfoStore]);

  useEffect(() => {
    setRelationBadgeCounts(undefined);
    if (!remotesReady || !relationMenu) return;
    void loadRelationBadges();
  }, [loadRelationBadges, relationMenu, remotesReady, tenantID]);

  if (!remotesReady) {
    return (
      <Flex
        vertical
        align="center"
        gap="middle"
        style={{ padding: 48 }}
      >
        <Spin />
        <Typography.Text type="secondary">{t('Account:tenantRoute.adminInitializing')}</Typography.Text>
      </Flex>
    );
  }

  if ((store.loading || !store.response) && !isSuccess(store.response)) {
    return (
      <Flex
        vertical
        align="center"
        gap="middle"
        style={{ padding: 48 }}
      >
        <Spin />
        <Typography.Text type="secondary">{t('Account:tenantRoute.adminLoading')}</Typography.Text>
      </Flex>
    );
  }

  if (!isSuccess(store.response)) {
    return (
      <Result
        extra={<Button icon={<ReloadOutlined />} type="primary" onClick={() => void store.fetch()}>{t('Common:retry')}</Button>}
        status="warning"
        subTitle={t('Account:tenantRoute.adminErrorDesc')}
        title={t('Account:tenantRoute.adminError')}
      />
    );
  }

  return (
    <AdminLayout
      menuBadges={relationMenu && relationBadgeCounts?.total !== '0'
        ? { [relationMenu.key]: relationBadgeCounts?.total ?? '0' }
        : undefined}
      menus={menus}
      tenantID={tenantID}
      tenantName={tenantInfo?.nameLocal || tenantInfo?.name}
    >
      {pageHeaderTarget => (
        <TenantAdminContent
          menus={menus}
          pageHeaderTarget={pageHeaderTarget}
          relationBadgeCounts={relationMenu ? relationBadgeCounts : undefined}
          tenantID={tenantID}
          onRefreshRelationBadges={relationMenu ? refreshRelationBadges : undefined}
        />
      )}
    </AdminLayout>
  );
});

/** AuditCompatibilityRedirect 将旧审核地址替换为规范路由并完整保留查询与哈希。 */
function AuditCompatibilityRedirect() {
  const location = useLocation();
  return <Navigate replace to={{ pathname: '/workspace/audit', search: location.search, hash: location.hash }} />;
}

/** TenantRoutes 声明租户 Workspace/Admin 两个公共壳层，并只保留真正的审核历史入口。 */
export function TenantRoutes({
  remotesReady,
  tenantID,
}: {
  remotesReady: boolean;
  tenantID: string;
}) {
  useLayoutEffect(() => {
    // 桌面宽度施加在根节点，确保挂载到 body 的弹层与业务布局使用同一页面宽度。
    document.documentElement.classList.add(TENANT_DESKTOP_PAGE_CLASS);
    return () => document.documentElement.classList.remove(TENANT_DESKTOP_PAGE_CLASS);
  }, []);
  return (
    <Routes>
      <Route element={<Navigate replace to="/workspace" />} path="/" />
      <Route element={<AuditCompatibilityRedirect />} path="/audit" />
      <Route element={<TenantWorkspacePage remotesReady={remotesReady} tenantID={tenantID} />} path="/workspace/*" />
      <Route element={<TenantAdminPage remotesReady={remotesReady} tenantID={tenantID} />} path="/admin/*" />
      <Route element={<Navigate replace to="/workspace" />} path="*" />
    </Routes>
  );
}

/** TenantEntry 解析租户前缀、建立会话上下文并进入租户内层路由。 */
export const TenantEntry = observer(({ remotesReady }: { remotesReady: boolean }) => {
  const location = useMiniLocation();
  const navigate = useMiniNavigate();
  const tenantID = useMemo(
    () => location.pathname.replace('/t/', '').split('/')[0] ?? '',
    [location.pathname],
  );
  const redirect = `${location.pathname}${location.search}${location.hash}`;

  useLayoutEffect(() => {
    setTenantID(tenantID);
  }, [tenantID]);

  useEffect(() => {
    const encodedRedirect = encodeURIComponent(redirect);
    // 租户内路由只检查本地凭证；过期凭证由 HTTP 401 统一恢复，避免路由挂载主动刷新登录态。
    if (!getUserToken()) {
      void navigate(`/login?redirect=${encodedRedirect}`);
      return;
    }
    if (!tenantID || !getStaffToken(tenantID)) {
      void navigate(`/tenant-selector?redirect=${encodedRedirect}`);
    }
  }, [navigate, redirect, tenantID]);

  if (!tenantID || !getUserToken() || !getStaffToken(tenantID)) return null;

  return (
    <PatternBrowserRouter base="/t">
      <TenantRoutes remotesReady={remotesReady} tenantID={tenantID} />
    </PatternBrowserRouter>
  );
});
